import React from 'react';
import { useQuiz } from '../contexts/QuizContext';
import { motion } from 'framer-motion';

const Offer = () => {
  const { diagnosis, answers } = useQuiz();
  
  // Determina o próximo passo com base na resposta do usuário (usado na seção de benefícios)
  const userGoal = answers['next_step'] || 'ter mais controle financeiro';

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="max-w-2xl mx-auto p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-primary-50 p-6 rounded-lg border-l-4 border-primary-500 mb-8">
            <h2 className="text-2xl font-bold text-primary-800 mb-2">Seu diagnóstico está pronto!</h2>
            <p className="text-primary-700">
              Baseado nas suas respostas, ficou claro que o que mais trava o crescimento do seu negócio hoje é <span className="font-bold">{diagnosis}</span>.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4">Agora imagine por um instante:</h3>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-start">
              <span className="text-primary-600 font-bold mr-2">👉</span>
              <p className="text-gray-700">
                Saber exatamente quanto seu negócio lucra de verdade — sem achar que "tá sobrando" quando na verdade o dinheiro some.
              </p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-600 font-bold mr-2">👉</span>
              <p className="text-gray-700">
                Ter clareza para decidir se pode contratar, investir, ou aumentar seu pró-labore sem medo de quebrar a empresa.
              </p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-600 font-bold mr-2">👉</span>
              <p className="text-gray-700">
                Ganhar tranquilidade, sem mais a sensação de estar "no escuro" em relação ao seu próprio caixa.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <p className="text-lg font-medium text-gray-800 mb-6">
            É isso que você vai conquistar para {userGoal} começando <span className="font-bold text-primary-600">em menos de 1 hora</span> com o <span className="font-bold text-primary-600">pacote Controle Fácil</span>:
          </p>

          <div className="bg-gray-50 p-6 rounded-lg mb-8">
            <div className="benefit-item">
              <div className="benefit-icon">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div>
                <p className="font-bold text-gray-800">Guia Prático (PDF)</p>
                <p className="text-gray-600">Passo a passo para separar definitivamente suas contas pessoais das empresariais e organizar o financeiro de forma simples, mesmo que você nunca tenha feito isso antes.</p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div>
                <p className="font-bold text-gray-800">Ferramenta Web Controle Fácil</p>
                <p className="text-gray-600">Uma plataforma intuitiva para monitorar entradas e saídas, controlar contas a pagar e a receber, visualizar relatórios e enxergar seu lucro real. É como ter um "painel de bordo" da sua empresa, direto no seu celular ou computador.</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
        >
          <div className="offer-highlight">
            <p className="text-gray-800">
              <span className="font-bold">💡 Com isso, você elimina {diagnosis.includes('separação de contas') ? 'a mistura de contas pessoais e empresariais' : 'as planilhas confusas'}</span> e passa a ter um <span className="font-bold">controle profissional do seu negócio</span>, sem depender só da memória, do banco ou de repasses do contador.
            </p>
          </div>

          <p className="text-lg text-gray-800 mt-6 mb-4">
            E aqui está o melhor: enquanto outras soluções do mercado cobram <span className="font-bold">R$50, R$100 por mês só pelo software</span>, você leva:
          </p>

          <ul className="list-disc pl-6 mb-6 space-y-1">
            <li className="text-gray-700">📘 O guia completo +</li>
            <li className="text-gray-700">💻 O acesso imediato à ferramenta +</li>
            <li className="text-gray-700">⚡ Os primeiros resultados já em poucos minutos de uso</li>
          </ul>

          <div className="bg-gray-50 p-6 rounded-lg text-center mb-8">
            <p className="text-lg text-gray-700 mb-2">Tudo isso por apenas</p>
            <div className="flex items-center justify-center space-x-3 mb-2">
              <span className="crossed-price text-xl">R$99,90</span>
              <span className="price-tag">R$39,90</span>
            </div>
            <p className="text-gray-600 text-sm">(pagamento único)</p>
          </div>

          <p className="text-gray-700 mb-8">
            A escolha é sua: continuar apagando incêndio nas finanças ou investir hoje o preço de um lanche para ter clareza, organização e tranquilidade real no seu negócio.
          </p>

          <a href="https://pay.kirvano.com/a523b72e-9c28-4ba1-b8b2-f932fd775b08" className="cta-button inline-block text-center">
            Sim, quero {diagnosis.includes('separação de contas') ? 'organizar minhas finanças' : 'meu Controle Fácil'} agora
          </a>
          
          <p className="text-xs text-gray-500 text-center mt-4">
            Pagamento 100% seguro | Acesso imediato após a compra
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Offer;
